import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { createFileRoute, useNavigate, useParams, useRouter } from '@tanstack/react-router'
import SFBackPageHeader from '@web/src/components/primatives/sf-back-page-header/sf-back-page-header'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import EventForm from '@web/src/features/event/event-form/event-form'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { LayersIcon } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  CreateEventInput,
  CreateEventMutation,
  CreateEventMutationVariables,
  CreateEventQuery,
} from 'types/graphql'

const CREATE_EVENT_QUERY: TypedDocumentNode<CreateEventQuery> = gql`
  query CreateEventQuery {
    eventTags {
      id
      name
    }
  }
`

const CREATE_EVENT_MUTATION: TypedDocumentNode<CreateEventMutation, CreateEventMutationVariables> = gql`
  mutation CreateEventMutation($input: CreateEventInput!) {
    createEvent(input: $input) {
      id
      slug
    }
  }
`

export const Route = createFileRoute('/(auth)/(main-layout)/event/create')({
  component: CreateEventPage,
  head: () =>
    seo({
      title: `Create Event | ${env.VITE_APP_NAME}`,
      description: 'Create a new Event entry',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loader: async ({ context }) => {
    const { queryRef } = await queryAndPreload(context, CREATE_EVENT_QUERY)
    return { queryRef }
  },
})

function CreateEventPage() {
  const navigate = useNavigate()
  const router = useRouter()
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const params = useParams({ strict: false })
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<CreateEventQuery>)
  const isInitialLoading = networkStatus === NetworkStatus.loading
  const { t } = useTranslation()

  const [createEvent] = useMutation(CREATE_EVENT_MUTATION, {
    onCompleted: (result) => {
      toast.success('Event created')
      navigate({
        to: '/event/$eventIdOrSlug',
        params: { eventIdOrSlug: result.createEvent.slug || result.createEvent.id },
      } as never)
      setShowSpinnerValue(false)
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
      setShowSpinnerValue(false)
    },
  })

  const onSave = (input: CreateEventInput) => {
    setShowSpinnerValue(true)
    createEvent({ variables: { input } })
  }

  const handleCancel = () => {
    router.history.back()
  }

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!data?.eventTags || data.eventTags.length === 0) {
    return <ErrorAlert error={new Error('Event tags not found')} />
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 pt-4">
      <div className="flex w-full flex-col gap-4">
        <SFBackPageHeader
          lowerText={
            params?.groupName ? (
              <div className="flex flex-row items-center gap-2">
                <LayersIcon className="size-5 shrink-0" />
                <span>Group: {params?.groupName}</span>
              </div>
            ) : (
              t('Set up your new event details')
            )
          }
          onBackClick={handleCancel}
          title={params?.groupId ? t('Create Group Event') : t('Create Event')}
        />
        <EventForm eventTags={data.eventTags} groupId={params?.groupId} onSave={onSave} showSpinner={showSpinner} />
      </div>
    </div>
  )
}
